import type { ImageComponentData } from './../ricos-content';
import { IMAGE_TYPE } from './../ricos-content';
import { unreachable } from './../ricos-content/ts-utils';
import type { PluginServices } from './../ricos-plugins/editorPlugins';
import type { PublicCommands } from './../ricos-types';
import { logInvariant } from './../_shared/log-invariant';
import type { PickMediaFn } from './../_shared/media/pick-media';
import type { PmImageNode } from './../_shared/pm-node-types';
import { upload } from './../_shared/upload/upload';

import { RICOS_DEFAULTS } from './consts';
import type { ImagePluginEditorConfig } from './types';

const getImagePickMedia = ({
  services,
  config,
}: {
  services: Pick<PluginServices, 'devService' | 'uploadService'>;
  config: ImagePluginEditorConfig;
}): PickMediaFn => {
  const { handleFileUpload, handleFileSelection } = config;
  const devPickMedia = services.devService().pickMedia;

  const pickMedia: PickMediaFn = devPickMedia
    ? devPickMedia
    : handleFileSelection
    ? async ({ multiple }) => {
        const data = await new Promise<ImageComponentData[]>(resolve =>
          handleFileSelection(
            undefined,
            multiple,
            ({ data }) => resolve(data),
            undefined,
            RICOS_DEFAULTS
          )
        );
        return { type: 'ready-media' as const, media: data };
      }
    : async ({ mimeType, multiple }) => {
        const files =
          (await services.uploadService?.selectFiles({ accept: mimeType, multiple })) ?? [];
        return { type: 'file' as const, file: files, customUploadFn: handleFileUpload };
      };
  return pickMedia;
};

export const handleImageUpload = async ({
  services,
  config,
  publicCommands,
  nodeId,
  files,
  pos,
}: {
  services: Pick<PluginServices, 'devService' | 'uploadService'>;
  config: ImagePluginEditorConfig;
  publicCommands: PublicCommands;
  nodeId?: string;
  files?: File[];
  pos?: number;
}) => {
  const { accept = 'image/*', multi = true, handleFileUpload } = config;

  const multiple = nodeId ? false : multi;

  const pickMedia = getImagePickMedia({ services, config });

  const result = files
    ? {
        type: 'file' as const,
        file: files,
        customUploadFn: handleFileUpload,
      }
    : await pickMedia({ mimeType: accept, multiple });

  switch (result.type) {
    case 'ready-media': {
      if (nodeId) {
        const componentData: PmImageNode['attrs'] = publicCommands?.getBlockComponentData(nodeId);

        const { file_name, width, height } = (result.media[0] as ImageComponentData) || {};
        publicCommands?.setBlock(nodeId, IMAGE_TYPE, {
          ...componentData,
          image: { src: { id: file_name }, width, height },
          tempData: null,
          loading: null,
          error: null,
        } satisfies PmImageNode['attrs']);
        return;
      }

      result.media.forEach((item, index) => {
        setTimeout(() => {
          const { file_name, width, height } = item as ImageComponentData;
          publicCommands.insertBlockWithBlankLines(
            IMAGE_TYPE,
            { image: { src: { id: file_name }, width, height }, ...RICOS_DEFAULTS },
            {
              updateSelection: result.media.length === index + 1,
            }
          );
        });
      });
      return;
    }
    case 'file': {
      const resultFiles = result.file;

      // when inserting (pasting) specific files, we want to skip blank lines before and after the block
      const insertOptions = files ? { skipBlankLineBefore: true, skipBlankLineAfter: true } : {};

      return resultFiles.map(async (file, index) => {
        // this can be executed inside tiptap command (when pasting) so we need to schedule file handling.
        setTimeout(async () => {
          const targetNodeId =
            nodeId ??
            publicCommands.insertBlockWithBlankLines(IMAGE_TYPE, RICOS_DEFAULTS, {
              updateSelection: resultFiles.length === index + 1,
              ...insertOptions,
              pos,
            });

          const trackBi = services.uploadService.trackUploadBi(IMAGE_TYPE, file.size, file.name);
          try {
            const url = URL.createObjectURL(file);
            await Promise.resolve();

            publicCommands?.setBlock(targetNodeId, IMAGE_TYPE, {
              ...publicCommands?.getBlockComponentData(targetNodeId),
              image: undefined,
              tempData: url,
              loading: true,
              error: null,
            });

            trackBi?.start();
            const uploadFn = result.customUploadFn || handleFileUpload;

            if (!uploadFn) {
              logInvariant(Error('No upload function provided'));
              throw new Error('No upload function provided');
            }

            const uploadedFile = await upload(uploadFn, file);
            const { file_name, width, height } = (uploadedFile.data as ImageComponentData) || {};
            publicCommands?.setBlock(targetNodeId, IMAGE_TYPE, {
              ...publicCommands?.getBlockComponentData(targetNodeId),
              image: { src: { id: file_name }, width, height },
              tempData: null,
              loading: null,
              error: null,
            } satisfies PmImageNode['attrs']);

            URL.revokeObjectURL(url as string);
            trackBi?.success();
            return;
          } catch (error: any) {
            const componentData = publicCommands?.getBlockComponentData(targetNodeId);
            publicCommands?.setBlock(targetNodeId, IMAGE_TYPE, {
              ...componentData,
              error,
              loading: null,
            });

            trackBi?.error(error);
            services.uploadService.notifyError(error);
            return;
          }
        });
      });
    }
    case 'cancel': {
      return;
    }
    default: {
      console.error(unreachable(result));
      return;
    }
  }
};
